import { forwardRef, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { Section, type SectionTone } from "./Section";
import { DisplayHeading } from "./DisplayHeading";

export interface CTABannerProps {
  headline: ReactNode;
  subhead?: ReactNode;
  primary: { label: string; to: string };
  secondary?: { label: string; to: string };
  tone?: SectionTone;
  className?: string;
}

export const CTABanner = forwardRef<HTMLElement, CTABannerProps>(function CTABanner(
  { headline, subhead, primary, secondary, tone = "ink", className },
  ref,
) {
  const onDark = tone === "ink" || tone === "accent";
  return (
    <Section
      ref={ref}
      tone={tone}
      padding="md"
      className={cn(className)}
    >
      <div className="flex flex-col items-start gap-8 md:flex-row md:items-end md:justify-between">
        <div className="max-w-2xl">
          <DisplayHeading level={2} className={onDark ? "text-paper" : undefined}>
            {headline}
          </DisplayHeading>
          {subhead && (
            <p className={cn("mt-4 text-lg leading-relaxed", onDark ? "text-paper/80" : "text-muted-foreground")}>
              {subhead}
            </p>
          )}
        </div>
        <div className="flex flex-wrap gap-3">
          <Link
            to={primary.to}
            className={cn(
              "inline-flex h-12 items-center gap-2 px-6 text-sm font-semibold uppercase tracking-wider transition-opacity hover:opacity-90",
              tone === "accent"
                ? "bg-ink text-paper"
                : "bg-accent-press text-accent-press-foreground",
            )}
          >
            {primary.label}
            <ArrowRight className="h-4 w-4" />
          </Link>
          {secondary && (
            <Link
              to={secondary.to}
              className={cn(
                "inline-flex h-12 items-center px-6 text-sm font-semibold uppercase tracking-wider transition-colors",
                onDark
                  ? "border border-paper/40 text-paper hover:border-paper hover:bg-paper hover:text-ink"
                  : "border border-rule text-foreground hover:bg-foreground hover:text-background",
              )}
            >
              {secondary.label}
            </Link>
          )}
        </div>
      </div>
    </Section>
  );
});
